import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import { Banknote, Loader2, AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";

const PLACE_CLS = {
  "1": "bg-[#B8860B]/15 text-[#B8860B]",
  "2": "bg-slate-400/20 text-slate-500",
  "3": "bg-[#CD7F32]/15 text-[#CD7F32]",
};

export default function Bank() {
  const m = useIsMobile();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [event, setEvent] = useState("");

  useEffect(() => {
    api.get("/me/bank").then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  }, []);

  const rows = useMemo(() => {
    const list = data?.rows || [];
    return event ? list.filter((r) => r.event === event) : list;
  }, [data, event]);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const headBorder = m ? "border-white/10" : "border-[#E4E7EB]";
  const hasName = data && data.competitor_name;
  const disc = data?.discrepancy || 0;
  const off = Math.abs(disc) >= 1;

  return (
    <div className={page} data-testid="bank-page">
      <div className={`px-4 sm:px-6 py-5 border-b ${headBorder}`}>
        <div className="flex items-center gap-2">
          <Banknote className="w-6 h-6 text-[#3B6B1E]" />
          <div>
            <h1 className={`font-display font-bold text-xl ${title}`}>Bank</h1>
            <p className="text-xs text-slate-400">{data?.competitor_name ? `${data.competitor_name} · ` : ""}every cheque, audited against the standings</p>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 max-w-3xl space-y-4">
        {data === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : !hasName ? (
          <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
            <Banknote className="w-8 h-8 mx-auto mb-3 text-[#3B6B1E]" />
            <div className={`font-display font-bold ${title}`}>Set your competitor name first</div>
            <p className="text-sm text-slate-400 mt-1 mb-4">Add your name on the Goals tab to pull every cheque you've cashed and audit it against the standings.</p>
            <button onClick={() => nav("/goals")} data-testid="bank-set-name" className="text-sm font-semibold px-3 py-2 rounded-lg bg-[#C8000F]/10 text-[#C8000F]">Go to Goals →</button>
          </div>
        ) : (
          <>
            {/* Totals + audit */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className={`rounded-xl border p-4 ${card}`} data-testid="bank-total">
                <div className="text-[11px] text-slate-400 uppercase tracking-wide">Won (from results)</div>
                <div className="text-2xl font-display font-bold text-[#3B6B1E] mt-0.5">{money(data.total)}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{data.cheques} cheque{data.cheques === 1 ? "" : "s"}</div>
              </div>
              <div className={`rounded-xl border p-4 ${card}`} data-testid="bank-standings">
                <div className="text-[11px] text-slate-400 uppercase tracking-wide">CPRA standings</div>
                <div className={`text-2xl font-display font-bold mt-0.5 ${title}`}>{money(data.standings_total)}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">official season money</div>
              </div>
              <div className={`rounded-xl border p-4 ${off ? "border-[#C8000F]/40 bg-[#C8000F]/8" : (m ? "border-[#3B6B1E]/40 bg-[#3B6B1E]/8" : "border-[#3B6B1E]/40 bg-[#3B6B1E]/6")}`} data-testid="bank-audit">
                <div className="text-[11px] text-slate-400 uppercase tracking-wide flex items-center gap-1">
                  {off ? <AlertTriangle className="w-3.5 h-3.5 text-[#C8000F]" /> : <CheckCircle2 className="w-3.5 h-3.5 text-[#3B6B1E]" />} Audit
                </div>
                <div className={`text-2xl font-display font-bold mt-0.5 ${off ? "text-[#C8000F]" : "text-[#3B6B1E]"}`}>{disc > 0 ? "+" : ""}{money(disc)}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{off ? "standings vs your cheques — worth a double-check" : "matches your cheques"}</div>
              </div>
            </div>

            {/* Per-event chips */}
            {data.by_event?.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="bank-events">
                <button onClick={() => setEvent("")} data-testid="bank-event-all"
                  className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg ${!event ? "bg-[#C8000F] text-white" : m ? "bg-white/5 text-slate-300" : "bg-[#F1F3F5] text-slate-600"}`}>All ({money(data.total)})</button>
                {data.by_event.map((e) => (
                  <button key={e.event} onClick={() => setEvent(e.event)} data-testid={`bank-event-${e.event.replace(/\s/g, "-")}`}
                    className={`shrink-0 text-xs font-bold px-3 py-2 rounded-lg ${event === e.event ? "bg-[#C8000F] text-white" : m ? "bg-white/5 text-slate-300" : "bg-[#F1F3F5] text-slate-600"}`}>{e.event} · {money(e.earnings)}</button>
                ))}
              </div>
            )}

            {/* Cheque ledger */}
            {rows.length === 0 ? (
              <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
                <div className={`font-display font-bold ${title}`}>No results yet</div>
                <p className="text-sm text-slate-400 mt-1">Your placings will show here as results are posted for the season.</p>
              </div>
            ) : (
              <div className={`rounded-xl border overflow-hidden ${card}`} data-testid="bank-ledger">
                {rows.map((r, i) => {
                  const pc = PLACE_CLS[String(r.place)] || "bg-slate-400/15 text-slate-400";
                  return (
                    <button key={i} onClick={() => r.rodeo_id && nav(`/rodeo/${r.rodeo_id}`)} data-testid={`bank-row-${i}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 text-left ${m ? "border-white/5 hover:bg-white/5" : "border-[#F1F3F5] hover:bg-[#FAFBFC]"}`}>
                      <span className={`text-[11px] font-bold w-10 text-center py-1 rounded-md shrink-0 ${pc}`}>{r.place ? `${r.place}` : "—"}</span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold truncate flex items-center gap-1 ${title}`}>{r.rodeo_name}<ChevronRight className="w-3.5 h-3.5 text-slate-300" /></div>
                        <div className="text-[11px] text-slate-400 truncate">{r.event}{r.value ? ` · ${r.value}` : ""}{r.date ? ` · ${fmtDate(r.date)}` : ""}</div>
                      </div>
                      <span className={`font-display font-bold text-sm w-24 text-right ${r.earnings > 0 ? "text-[#3B6B1E]" : "text-slate-400"}`}>{r.earnings > 0 ? money(r.earnings) : "—"}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-slate-400">Tip: CPRA/PRCA sometimes adjust payouts after doctor releases — if the audit number isn't $0, double-check the rodeo that changed.</p>
          </>
        )}
      </div>
    </div>
  );
}
